/**
 * Promotion card — let the fill animation finish before following the link.
 *
 * On a touch device a tap synthesizes `:hover`, so the fill starts and the copy
 * flips to the inverse colour. The browser then navigates while the fill is
 * still travelling, and the synthesized hover is dropped on touchend — leaving
 * inverse-coloured copy on an unfilled card for as long as the next page takes
 * to paint, which reads as the text having vanished.
 *
 * So on touch we hold the tap: drive the fill from a class (the synthesized
 * hover cannot be relied on to survive), let it complete, then follow the link.
 *
 * Pointer devices are untouched — they have already seen the whole animation on
 * hover before the click lands, and delaying them would just feel slow.
 *
 * Progressive enhancement: this only ever cancels a navigation it then performs
 * itself, so without JS the link behaves normally.
 */
(() => {
  if (window.promotionCardTapHoldReady) return;
  window.promotionCardTapHoldReady = true;

  const LINK = '.promotion-card__overlay-link';
  const CARD = '.promotion-card';
  const FILLING = 'promotion-card--filling';
  const FALLBACK_MS = 600;

  // `hover: none` is the question that matters here — not screen size. A device
  // that can hover already showed the animation before the click.
  if (!window.matchMedia('(hover: none)').matches) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Delegated: cards are rendered server-side into the product grid, and a
  // single listener survives any later re-render of that grid. On `window` in
  // capture phase so it runs before boost-sd-custom.js stops promo clicks at
  // `document` (to keep Boost's card handler from sending them to the homepage).
  window.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const link = event.target.closest && event.target.closest(LINK);
    if (!link || !link.href) return;

    const card = link.closest(CARD);
    // A second tap while the fill is running must not queue another navigation.
    if (!card || card.classList.contains(FILLING)) return;

    event.preventDefault();
    card.classList.add(FILLING);

    window.setTimeout(() => {
      window.location.href = link.href;
    }, readFillDuration(card));
  }, true);

  /** Reads --promotion-fill-duration, which CSS owns, in ms. */
  function readFillDuration(card) {
    const raw = getComputedStyle(card).getPropertyValue('--promotion-fill-duration').trim();
    if (raw.endsWith('ms')) return parseFloat(raw) || FALLBACK_MS;
    if (raw.endsWith('s')) return (parseFloat(raw) || 0) * 1000 || FALLBACK_MS;
    return FALLBACK_MS;
  }
})();
